import { useState } from "react";
import { RefreshCw, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { externalSupabase } from "@/lib/externalSupabase";
import { useToast } from "@/hooks/use-toast";

interface GallerySyncProps {
  userEmail?: string;
}

const GallerySync = ({ userEmail }: GallerySyncProps) => {
  const [syncing, setSyncing] = useState(false);
  const [total, setTotal] = useState(0);
  const [done, setDone] = useState(0);
  const [failed, setFailed] = useState(0);
  const [status, setStatus] = useState<string | null>(null);
  const { toast } = useToast();

  const handleSync = async () => {
    setSyncing(true);
    setDone(0);
    setFailed(0);
    setStatus("Fetching all properties…");

    // Fetch ALL properties, then filter client-side for empty/null gallery_urls
    const { data: rows, error } = await (externalSupabase
      .from("properties") as any)
      .select("id, airbnb_url, gallery_urls, image_url")
      .order("created_at", { ascending: false });

    if (error || !rows) {
      setStatus(`Error: ${error?.message || "No data"}`);
      setSyncing(false);
      return;
    }

    // Filter for rows where gallery has fewer than 6 images
    const toSync = rows.filter((r: any) =>
      !r.gallery_urls || r.gallery_urls.length < 6 || r.gallery_urls.every((u: string) => !u)
    );
    setTotal(toSync.length);

    if (toSync.length === 0) {
      setStatus("All properties already have 6+ gallery images!");
      toast({ title: "Nothing to sync", description: "All properties already have 6+ gallery photos." });
      setSyncing(false);
      return;
    }

    setStatus(`Syncing ${toSync.length} properties…`);

    let successCount = 0;
    let failCount = 0;

    for (const prop of toSync) {
      try {
        const res = await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sync-gallery`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json", apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY },
            body: JSON.stringify({ airbnb_url: prop.airbnb_url, property_id: prop.id }),
          }
        );
        const result = await res.json();

        // Skip if the function said images weren't good enough
        if (result.skipped) {
          console.log(`[GallerySync] Skipped ${prop.id} — not enough quality images`);
          failCount++;
          setDone(successCount + failCount);
          setFailed(failCount);
          continue;
        }

        if (result.gallery_urls && result.gallery_urls.length > 0) {
          // Save original image_url to localStorage before overwriting
          if (prop.image_url) {
            const key = `original_hero_${prop.id}`;
            if (!localStorage.getItem(key)) {
              localStorage.setItem(key, prop.image_url);
            }
          }

          const updatePayload: any = { gallery_urls: result.gallery_urls };
          if (result.image_url) updatePayload.image_url = result.image_url;

          const { error: updateErr } = await (externalSupabase.from("properties") as any)
            .update(updatePayload)
            .eq("id", prop.id);
          
          if (updateErr) {
            console.error(`[GallerySync] Update failed for ${prop.id}:`, updateErr);
            failCount++;
          } else {
            successCount++;
          }
        } else {
          console.warn(`[GallerySync] No images found for ${prop.id} (${prop.airbnb_url})`);
          failCount++;
        }
      } catch (err) {
        console.error(`[GallerySync] Error for ${prop.id}:`, err);
        failCount++;
      }

      setDone(successCount + failCount);
      setFailed(failCount);
    }

    const finalStatus = `Done! ${successCount} updated, ${failCount} failed.`;
    setStatus(finalStatus);
    setSyncing(false);

    if (successCount > 0) {
      toast({
        title: "Photos Updated!",
        description: `Successfully filled gallery photos for ${successCount} properties.`,
      });
    } else {
      toast({
        title: "Sync Complete",
        description: "No new photos were found for the remaining properties.",
        variant: "destructive",
      });
    }
  };

  const progress = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div className="rounded-2xl border-2 border-destructive bg-destructive/5 p-6 mb-8">
      <div className="flex items-center gap-2 mb-4">
        <span className="text-xs font-bold uppercase tracking-wider text-destructive bg-destructive/10 px-2 py-0.5 rounded">Admin Tools</span>
      </div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-display font-semibold text-foreground">Photo Sync</h3>
          <p className="text-xs text-muted-foreground">Re-scrape photos for properties with fewer than 6 gallery images</p>
        </div>
        <Button
          onClick={handleSync}
          disabled={syncing}
          size="sm"
          className="gap-2"
        >
          {syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          {syncing ? "Syncing…" : "Update Existing Photos"}
        </Button>
      </div>

      {(syncing || status) && (
        <div className="space-y-3">
          {total > 0 && (
            <>
              <Progress value={progress} className="h-2" />
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>{done} / {total} processed</span>
                <span>{progress}%</span>
              </div>
            </>
          )}
          {status && (
            <div className="flex items-center gap-2 text-sm">
              {syncing ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
              ) : failed > 0 ? (
                <AlertCircle className="w-3.5 h-3.5 text-destructive" />
              ) : (
                <CheckCircle className="w-3.5 h-3.5 text-primary" />
              )}
              <span className="text-muted-foreground">{status}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GallerySync;
